import { Emoji, FlexBox, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'

import noteEditMenu from 'assets/images/homepage/callout_main/edit-menu-note.webp'
import moderatorGraphic from 'assets/images/homepage/callout_main/yes-graphic.webp'
import textOutline from 'assets/images/homepage/callout_main/outline-green.webp'
import { useScroll, useTransform, motion } from 'framer-motion'
import { useRef } from 'react'
import { MLPDownloadAppCTA } from 'components/marketing_landing_page'

interface Props {
  description?: string
}

export const MLPClubCalloutText = ({
  description = 'Explore thousands of free online book clubs led by authors, book influencers, and celebrities.',
}: Props) => {
  const { colors, mediaQueries } = useTheme()
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['10% end', 'start center'],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0, 1])
  const noteY = useTransform(scrollYProgress, [0, 1], [50, 0])
  const moderatorY = useTransform(scrollYProgress, [0, 1], [200, 0])
  const emojiY = useTransform(scrollYProgress, [0, 1], [100, 0])

  return (
    <FlexBox
      className={css`
        position: relative;
        gap: max(1.389vw, 16px);
        ${mediaQueries.desktop} {
          gap: 24px;
        }
      `}
      flexDirection="column"
      alignItems="center"
      color={colors.uiGraphite}
      ref={ref}
    >
      <motion.img
        style={{ opacity, y: noteY }}
        className={css`
          display: none;
          ${mediaQueries.mobileL} {
            display: block;
            width: 67px;
            position: absolute;
            inset: -34px auto auto -34px;
          }
          ${mediaQueries.tablet} {
            inset: -24px auto auto -34px;
          }
        `}
        src={noteEditMenu}
        alt="note edit menu"
      />
      <motion.img
        style={{ opacity, y: moderatorY }}
        className={css`
          display: none;
          ${mediaQueries.mobileL} {
            display: block;
            width: 56px;
            position: absolute;
            inset: -44px 34px auto auto;
          }
          ${mediaQueries.tablet} {
            width: 86px;
            inset: -64px 34px auto auto;
          }
        `}
        src={moderatorGraphic}
        alt="club moderator graphic"
      />
      <motion.div
        style={{ opacity, y: emojiY }}
        className={css`
          display: none;
          ${mediaQueries.mobileL} {
            display: block;
            position: absolute;
            inset: 80% 0 auto auto;
            transform: translateY(-50%);
          }
          ${mediaQueries.desktop} {
            inset: 50% -42px auto auto;
            transform: translateY(-50%);
          }
        `}
      >
        <Emoji emoji="👍" size={29} />
      </motion.div>
      <Text
        className={css`
          position: relative;
          max-width: 278px;
          ${mediaQueries.mobileL} {
            max-width: 38.281vw;
          }
          ${mediaQueries.desktop} {
            max-width: 441px;
          }
        `}
        tag="h2"
        type="header"
        align="center"
        custom={{
          fontSize: '36px',
          lineHeight: '36px',
          letterSpacing: '-0.5px',
          [mediaQueries.mobileL]: {
            fontSize: '4.861vw',
            lineHeight: '4.861vw',
          },
          [mediaQueries.desktop]: {
            fontSize: '56px',
            lineHeight: '56px',
          },
        }}
      >
        Join other readers inside the&nbsp;
        <span
          className={css`
            position: relative;
          `}
        >
          same book
          <img
            className={css`
              display: block;
              width: 110%;
              position: absolute;
              inset: 50% auto auto 50%;
              transform: translate(-50%, -50%);
            `}
            src={textOutline}
            alt="future text outline"
          />
        </span>
      </Text>
      <Text
        className={css`
          max-width: max(36.111vw, 317px);
          ${mediaQueries.desktop} {
            max-width: 416px;
          }
        `}
        type="body"
        align="center"
        color={colors.uiGraphite}
        custom={{
          fontSize: 'max(1.563vw, 14px)',
          lineHeight: 'max(2.083vw, 20px)',
          letterSpacing: '-0.2px',
          [mediaQueries.desktop]: {
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '-0.3px',
          },
        }}
      >
        {description}
      </Text>
      <MLPDownloadAppCTA
        buttonStyles={css`
          background-color: ${colors.uiGreenUltraLight};
          border-color: ${colors.uiGreenUltraLight};
          color: ${colors.uiGraphite};
          &:hover {
            background-color: ${colors.uiGreenLight};
            border-color: ${colors.uiGreenLight};
            color: ${colors.uiGraphite};
          }
        `}
      />
    </FlexBox>
  )
}
